import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ==================== Types ====================

export interface AdminStats {
  templates: { total: number; enabled: number; disabled: number }
  styles: { total: number; enabled: number; disabled: number }
  categories: { total: number; enabled: number; disabled: number }
  generations: { today: number; thisWeek: number; thisMonth: number }
  users: { total: number; admins: number }
}

export interface DailyStatItem {
  date: string
  imageCount: number
  generationCount: number
  sharedCount: number
}

export interface DailyGenerationStats {
  dailyStats: DailyStatItem[]
  summary: {
    totalImages: number
    totalGenerations: number
    totalShared: number
    averagePerDay: number
    peakDay: {
      date: string
      count: number
    }
  }
}

export interface DailyUserStatItem {
  date: string
  newUsers: number
}

export interface DailyUserStats {
  dailyStats: DailyUserStatItem[]
  summary: {
    totalNewUsers: number
    averagePerDay: number
    peakDay: {
      date: string
      count: number
    }
  }
}

export interface DailyRevenueItem {
  date: string
  generationCount: number
  imageCount: number
  costKrw: number
  revenueKrw: number
  profitKrw: number
}

export interface RevenueStats {
  dailyStats: DailyRevenueItem[]
  summary: {
    totalCostKrw: number
    totalRevenueKrw: number
    totalProfitKrw: number
    averageRevenuePerDay: number
    marginPercent: number
  }
}

export interface HourlyStatItem {
  hour: number
  imageCount: number
  generationCount: number
}

export interface HourlyStats {
  date: string
  hourlyStats: HourlyStatItem[]
  summary: {
    totalImages: number
    totalGenerations: number
    peakHour: {
      hour: number
      count: number
    }
  }
}

export interface Template {
  id: string
  categoryId?: string
  name: string
  description?: string
  nameI18n?: Record<string, string>
  descriptionI18n?: Record<string, string>
  textPrompt: string
  textPromptI18n?: Record<string, string>
  styleId?: string
  promptSuffix?: string
  modelId?: string
  aspectRatio: string
  thumbnailS3Key?: string
  thumbnailUrl?: string | null
  listOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
  referenceImageCount?: number
  usageCount?: number
  category?: { id: string; name: string }
  style?: { id: string; name: string }
}

export interface TemplateDetail extends Template {
  references: Array<{
    id: string
    templateId: string
    s3Key: string
    thumbnailS3Key: string
    thumbnailUrl: string
    listOrder: number
    createdAt: string
  }>
}

export interface PaginatedTemplates {
  items: Template[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface Style {
  id: string
  name: string
  description: string
  nameI18n?: Record<string, string>
  descriptionI18n?: Record<string, string>
  promptSuffix: string
  thumbnailS3Key?: string
  thumbnailUrl?: string | null
  listOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface Category {
  id: string
  name: string
  description?: string
  nameI18n?: Record<string, string>
  descriptionI18n?: Record<string, string>
  listOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

/** 생성된 이미지 정보 */
export interface GeneratedImageInfo {
  s3Key: string
  seed: number
  imageUrl: string
}

export interface Generation {
  id: string // generationId
  userId: string
  prompt: string // 원본 사용자 프롬프트
  modelId: string
  style: string
  aspectRatio: string
  referenceImages?: Array<{
    s3Key: string
    thumbnailS3Key: string
    imageUrl: string
    thumbnailUrl: string
  }>
  generatedImages: GeneratedImageInfo[]
  costKrw: number
  usageLogId: number
  deletedAt: string | null
  createdAt: string
  user?: {
    id: string
    email: string
  }
  template?: {
    id: string
    name: string
    description: string | null
    thumbnailUrl: string | null
  } | null
}

export interface PaginatedGenerations {
  items: Generation[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface GenerationAttemptErrorLog {
  stage: 'reference-download' | 'image-generation' | 's3-upload' | 'reference-copy' | 'db-insert' | 'unexpected'
  message: string
  imageIndex?: number
  attempt?: number
  maxRetries?: number
  loggedAt: string
}

export interface GenerationAttempt {
  id: string
  generationId: string
  userId: string
  prompt: string
  modelId: string
  style: string
  aspectRatio: string
  referenceImages?: Array<{
    s3Key: string
    thumbnailS3Key: string
  }>
  generatedImages: Array<{
    s3Key: string
    seed: number
  }>
  templateId?: string | null
  requestedCount: number
  successCount: number
  failureCount: number
  errorSummary: string
  errorLogs: GenerationAttemptErrorLog[]
  createdAt: string
  user?: {
    id: string
    email: string
  } | null
  template?: {
    id: string
    name: string
  } | null
}

export interface GenerationAttemptDetail {
  id: string
  generationId: string
  userId: string
  prompt: string
  modelId: string
  style: string
  aspectRatio: string
  referenceImages?: Array<{
    s3Key: string
    thumbnailS3Key: string
    imageUrl: string
    thumbnailUrl: string
  }>
  generatedImages: Array<{
    s3Key: string
    seed: number
    imageUrl: string
  }>
  templateId?: string | null
  requestedCount: number
  successCount: number
  failureCount: number
  errorSummary: string
  errorLogs: GenerationAttemptErrorLog[]
  createdAt: string
  user?: {
    id: string
    email: string
  } | null
  template?: {
    id: string
    name: string
    description: string | null
    thumbnailUrl: string | null
  } | null
}

export interface PaginatedGenerationAttempts {
  items: GenerationAttempt[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ==================== API Client ====================

export const apiClient = api

// ==================== Types: Period ====================

export type Period =
  | { type: 'preset'; days: number }
  | { type: 'custom'; startDate: string; endDate: string }

function periodToQueryString(period: Period): string {
  if (period.type === 'custom') {
    return `startDate=${period.startDate}&endDate=${period.endDate}`
  }
  return `days=${period.days}`
}

// ==================== Hooks: Stats ====================

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => apiClient.get<AdminStats>('/admin/stats'),
  })
}

export function useDailyStats(period: Period) {
  return useQuery({
    queryKey: ['admin', 'stats', 'daily', period],
    queryFn: () => apiClient.get<DailyGenerationStats>(`/admin/stats/daily?${periodToQueryString(period)}`),
  })
}

export function useDailyUserStats(period: Period) {
  return useQuery({
    queryKey: ['admin', 'stats', 'daily-users', period],
    queryFn: () => apiClient.get<DailyUserStats>(`/admin/stats/daily-users?${periodToQueryString(period)}`),
  })
}

export function useRevenueStats(period: Period) {
  return useQuery({
    queryKey: ['admin', 'stats', 'revenue', period],
    queryFn: () => apiClient.get<RevenueStats>(`/admin/stats/revenue?${periodToQueryString(period)}`),
  })
}

export function useHourlyStats(date: string | null) {
  return useQuery({
    queryKey: ['admin', 'stats', 'hourly', date],
    queryFn: () => apiClient.get<HourlyStats>(`/admin/stats/hourly?date=${date}`),
    enabled: !!date,
  })
}

// ==================== Hooks: Templates ====================

export function useTemplates(params: {
  page?: number
  pageSize?: number
  search?: string
  enabled?: 'true' | 'false' | 'all'
  categoryId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.enabled) query.set('enabled', params.enabled)
  if (params.categoryId) query.set('categoryId', params.categoryId)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'templates', params],
    queryFn: () => apiClient.get<PaginatedTemplates>(`/admin/templates?${query}`),
  })
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['admin', 'templates', id],
    queryFn: () => apiClient.get<TemplateDetail>(`/admin/templates/${id}`),
    enabled: !!id,
  })
}

export function useCreateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Template>) => apiClient.post<Template>('/admin/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUpdateTemplate(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Template>) => apiClient.patch<Template>(`/admin/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUploadTemplateThumbnail(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dataUrl: string) =>
      apiClient.post<{ thumbnailS3Key: string; thumbnailUrl: string }>(
        `/admin/templates/${id}/thumbnail`,
        { dataUrl }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useAddTemplateReferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateId,
      images,
    }: {
      templateId: string
      images: Array<{ dataUrl: string; listOrder: number }>
    }) => apiClient.post(`/admin/templates/${templateId}/references`, { images }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', variables.templateId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useDeleteReference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (refId: string) => apiClient.delete(`/admin/references/${refId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

// ==================== Hooks: Styles ====================

export function useStyles() {
  return useQuery({
    queryKey: ['admin', 'styles'],
    queryFn: () => apiClient.get<Style[]>('/admin/styles'),
  })
}

export function useStyle(id: string) {
  return useQuery({
    queryKey: ['admin', 'styles', id],
    queryFn: () => apiClient.get<Style>(`/admin/styles/${id}`),
    enabled: !!id,
  })
}

export function useCreateStyle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Style>) => apiClient.post<Style>('/admin/styles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUpdateStyle(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Style>) => apiClient.patch<Style>(`/admin/styles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles', id] })
    },
  })
}

export function useDeleteStyle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/styles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUploadStyleThumbnail(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dataUrl: string) =>
      apiClient.post<{ thumbnailS3Key: string; thumbnailUrl: string }>(
        `/admin/styles/${id}/thumbnail`,
        { dataUrl }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles'] })
    },
  })
}

export function useReorderStyles() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      apiClient.post<{ success: boolean }>('/admin/styles/reorder', { orderedIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'styles'] })
    },
  })
}

// ==================== Hooks: Categories ====================

export function useCategories() {
  return useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => apiClient.get<Category[]>('/admin/categories'),
  })
}

export function useCategory(id: string) {
  return useQuery({
    queryKey: ['admin', 'categories', id],
    queryFn: () => apiClient.get<Category>(`/admin/categories/${id}`),
    enabled: !!id,
  })
}

export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Category>) => apiClient.post<Category>('/admin/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUpdateCategory(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Category>) => apiClient.patch<Category>(`/admin/categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories', id] })
    },
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

// ==================== Hooks: Generations ====================

export function useGenerations(params: {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'generations', params],
    queryFn: () => apiClient.get<PaginatedGenerations>(`/admin/generations?${query}`),
  })
}

export function useGenerationAttempts(params: {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'generation-attempts', params],
    queryFn: () => apiClient.get<PaginatedGenerationAttempts>(`/admin/generation-attempts?${query}`),
  })
}

export function useGenerationAttempt(id: string) {
  return useQuery({
    queryKey: ['admin', 'generation-attempts', id],
    queryFn: () => apiClient.get<GenerationAttemptDetail>(`/admin/generation-attempts/${id}`),
    enabled: !!id,
  })
}

export function useDeleteGenerationAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/generation-attempts/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generation-attempts'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'generation-attempts', id] })
    },
  })
}

export function useGeneration(id: string) {
  return useQuery({
    queryKey: ['admin', 'generations', id],
    queryFn: () => apiClient.get<Generation>(`/admin/generations/${id}`),
    enabled: !!id,
  })
}

export function useUpdateGeneration(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Generation>) =>
      apiClient.patch<Generation>(`/admin/generations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations', id] })
    },
  })
}

export function useDeleteGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/generations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

// ==================== Types: Settings ====================

export interface ModelSettings {
  defaultModelId: string | null
  modelFallbacks: Record<string, string>
}

// ==================== Hooks: Settings ====================

export function useModelSettings() {
  return useQuery({
    queryKey: ['admin', 'settings', 'models'],
    queryFn: () => apiClient.get<ModelSettings>('/admin/settings/models'),
  })
}

export function useUpdateModelSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      defaultModelId?: string
      modelFallbacks?: Record<string, string | null>
    }) => apiClient.patch<ModelSettings>('/admin/settings/models', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'models'] })
    },
  })
}

// ==================== Types/Hooks: Community Hidden Enterprises ====================

export type SsoSource = 'gov' | 'wrks'

export interface CommunityHiddenEntry {
  source: SsoSource
  enterpriseId: number
}

export interface CommunityHiddenEnterprises {
  entries: CommunityHiddenEntry[]
}

export function useCommunityHiddenEnterprises() {
  return useQuery({
    queryKey: ['admin', 'settings', 'community-hidden-enterprises'],
    queryFn: () =>
      apiClient.get<CommunityHiddenEnterprises>(
        '/admin/settings/community-hidden-enterprises'
      ),
  })
}

export function useUpdateCommunityHiddenEnterprises() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entries: CommunityHiddenEntry[]) =>
      apiClient.put<CommunityHiddenEnterprises>(
        '/admin/settings/community-hidden-enterprises',
        { entries }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'community-hidden-enterprises'],
      })
      // 어드민이 본인 (source, enterpriseId) 를 추가/삭제한 경우 즉시 본인 화면에 반영
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

// ==================== Types: Users ====================

export interface AdminUser {
  id: string
  email: string
  source: string
  isAdmin: boolean
  hasUsage: boolean
  enterpriseId: number | null
  workspaceId: number | null
  createdAt: string
  updatedAt?: string
}

export interface AdminUserDetail extends AdminUser {
  role: string | null
  /** 'oauth' = Wrks OAuth 2.0 grant (refresh token stored server-side), 'legacy' = wrksToken launch */
  authMode: 'oauth' | 'legacy'
  /** Masked (`…last6`) — the stored token is a live bearer credential and never leaves the backend */
  ssoTokenMasked: string | null
  tokenExpiresAt: string
  wrksRefreshExpiresAt: string | null
  wrksSub: string | null
}

export interface PaginatedUsers {
  items: AdminUser[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ==================== Types: Shared Images ====================

export interface AdminSharedImage {
  id: string
  userId: string
  userEmail: string | null
  generationId: string
  aspectRatio: string
  s3Key: string
  imageUrl: string
  prompt: string | null
  createdAt: string
}

export interface PaginatedSharedImages {
  items: AdminSharedImage[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ==================== Hooks: Shared Images ====================

export function useAdminSharedImages(params: {
  page?: number
  pageSize?: number
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'shared-images', params],
    queryFn: () => apiClient.get<PaginatedSharedImages>(`/admin/shared-images?${query}`),
  })
}

export interface AdminSharedImageDetail extends AdminSharedImage {
  generation: {
    id: string
    prompt: string | null
    style: string | null
    aspectRatio: string
    modelId: string | null
    referenceImages: Array<{ s3Key: string; thumbnailS3Key: string }> | null
    referenceImageUrls: string[] | null
    generatedImages: Array<{ s3Key: string; seed: number; imageUrl?: string }> | null
    createdAt: string
  } | null
  template: {
    id: string
    name: string
    description: string | null
    thumbnailUrl: string | null
  } | null
}

export function useAdminSharedImage(shareId: string) {
  return useQuery({
    queryKey: ['admin', 'shared-images', shareId],
    queryFn: () => apiClient.get<AdminSharedImageDetail>(`/admin/shared-images/${shareId}`),
    enabled: !!shareId,
  })
}

export function useAdminDeleteSharedImage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (shareId: string) => apiClient.delete(`/admin/shared-images/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'shared-images'] })
    },
  })
}

// ==================== Hooks: Users ====================

export function useUsers(params: {
  page?: number
  pageSize?: number
  search?: string
  isAdmin?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.isAdmin) query.set('isAdmin', params.isAdmin)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => apiClient.get<PaginatedUsers>(`/admin/users?${query}`),
  })
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => apiClient.get<AdminUserDetail>(`/admin/users/${id}`),
    enabled: !!id,
  })
}

export function useUpdateUser(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { isAdmin: boolean }) =>
      apiClient.patch<AdminUser>(`/admin/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

// ==================== Types/Hooks: Outage Notice ====================

export interface OutageNotice {
  enabled: boolean
  message: string
}

export function useOutageNotice() {
  return useQuery({
    queryKey: ['admin', 'settings', 'outage-notice'],
    queryFn: () => apiClient.get<OutageNotice>('/admin/settings/outage-notice'),
  })
}

export function useUpdateOutageNotice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notice: OutageNotice) =>
      apiClient.put<OutageNotice>('/admin/settings/outage-notice', notice),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'outage-notice'],
      })
      // /api/config 가 outage notice 를 포함하므로 캐시 무효화로 배너 즉시 반영
      queryClient.invalidateQueries({ queryKey: ['config'] })
    },
  })
}

// ==================== Hooks: Error Reports ====================

export interface AdminErrorGroupLatest {
  id: string
  name: string
  message: string
  errorType: string
  url: string | null
  buildId: string | null
  status: string
  createdAt: string
}

export interface AdminErrorGroup {
  fingerprint: string
  count: number
  userCount: number
  firstSeen: string
  lastSeen: string
  latest: AdminErrorGroupLatest | null
}

export interface AdminErrorOccurrence {
  id: string
  userEmail: string | null
  url: string | null
  buildId: string | null
  origin: string
  createdAt: string
}

export interface AdminErrorReportDetail {
  id: string
  userId: string
  userEmail: string | null
  sessionId: string | null
  fingerprint: string
  name: string
  message: string
  stack: string | null
  // 압축된 stack 을 build 별 소스맵으로 복원한 결과 (백엔드가 조회 시 1회 채움). null = 복원 불가/미시도.
  stackSymbolicated: string | null
  errorType: string
  url: string | null
  route: string | null
  userAgent: string | null
  buildId: string | null
  language: string | null
  viewport: string | null
  breadcrumbs: { ts: number; type: string; message: string; data?: Record<string, unknown> }[] | null
  context: Record<string, unknown> | null
  origin: string
  status: string
  createdAt: string
  occurrences: AdminErrorOccurrence[]
}

export function useAdminErrorGroups(params: {
  page?: number
  pageSize?: number
  status?: string
  search?: string
  days?: number
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.status) query.set('status', params.status)
  if (params.search) query.set('search', params.search)
  if (params.days) query.set('days', String(params.days))

  return useQuery({
    queryKey: ['admin', 'error-reports', params],
    queryFn: () =>
      apiClient.get<{ items: AdminErrorGroup[]; total: number; page: number; pageSize: number }>(
        `/admin/error-reports?${query}`,
      ),
  })
}

export function useAdminErrorReport(id: string) {
  return useQuery({
    queryKey: ['admin', 'error-reports', id],
    queryFn: () => apiClient.get<AdminErrorReportDetail>(`/admin/error-reports/${id}`),
    enabled: !!id,
  })
}

export function useAdminResolveErrorFingerprint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fingerprint: string) =>
      apiClient.patch<{ fingerprint: string; status: string }>(
        `/admin/error-reports/fingerprints/${fingerprint}/resolve`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'error-reports'] })
    },
  })
}

export function useAdminDeleteErrorFingerprint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fingerprint: string) =>
      apiClient.delete<{ fingerprint: string; deleted: number }>(
        `/admin/error-reports/fingerprints/${fingerprint}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'error-reports'] })
    },
  })
}

// ==================== AI template review ====================
export type CandidateDraft = {
  name: string; description: string; categoryId: string; textPrompt: string; promptSuffix: string
  modelId: string; aspectRatio: string; referenceAssets: string[]
  nameI18n: Record<string, string>; descriptionI18n: Record<string, string>; textPromptI18n: Record<string, string>
}
export type CandidateEvaluation = {
  fidelity: number; visual: number; textAccuracy: number; reusability: number
  criticalDefects: string[]; rationale: string
}
export type CandidateExperiment = {
  case: number; values: Record<string, string>; prompt: string; referenceAssets: string[]
  imageAsset: string | null; evaluation: CandidateEvaluation | null; error: string | null
}
export type CandidatePayload = {
  draft: CandidateDraft; purpose: string; sources: { url: string; reason: string }[]
  experiments: CandidateExperiment[]; thumbnailAsset: string | null
}
export type TemplateCandidate = {
  id: string; status: string; version: number; templateId: string | null
  createdAt: string; updatedAt: string; payload: CandidatePayload | null; passed: boolean | null
  costKrw: number; assets: { id: string; url: string | null }[]
}
export type TemplateCandidateDetailData = TemplateCandidate & {
  revisions: { version: number; payload: CandidatePayload; passed: boolean; createdAt: string }[]
  reviews: { version: number; action: string; note: string; createdAt: string }[]
}
export type TemplateAgentSummary = {
  paused: boolean; weeklyLimitKrw: number; spentKrw: number; pending: number; submitted: number; published: number
  lastRun: { status: string; updatedAt: string; summary: { reason?: string; failures?: string[] } } | null
}
const TEMPLATE_CANDIDATES_ROOT = '/admin/template-candidates'

export function useTemplateCandidates(status: string, page: number) {
  return useQuery({ queryKey: ['template-candidates', status, page], queryFn: () => api.get<{ items: TemplateCandidate[]; total: number }>(`${TEMPLATE_CANDIDATES_ROOT}?status=${encodeURIComponent(status)}&page=${page}`), refetchInterval: 30000 })
}
export function useTemplateAgentSummary() {
  return useQuery({ queryKey: ['template-agent-summary'], queryFn: () => api.get<TemplateAgentSummary>(`${TEMPLATE_CANDIDATES_ROOT}/summary`), refetchInterval: 30000 })
}
export function useControlTemplateAgent() {
  const client = useQueryClient()
  return useMutation({ mutationFn: (paused: boolean) => api.patch(`${TEMPLATE_CANDIDATES_ROOT}/summary`, { paused }), onSuccess: () => client.invalidateQueries({ queryKey: ['template-agent-summary'] }) })
}
export function useTemplateCandidate(id: string) {
  return useQuery({ queryKey: ['template-candidates', id], queryFn: () => api.get<TemplateCandidateDetailData>(`${TEMPLATE_CANDIDATES_ROOT}/${id}`) })
}
export function useReviewTemplateCandidate(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { action: string; expectedVersion: number; note: string; draft?: CandidateDraft; thumbnailAsset?: string }) => api.post<{ templateId: string | null }>(`${TEMPLATE_CANDIDATES_ROOT}/${id}/review`, body),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['template-candidates'] })
      void client.invalidateQueries({ queryKey: ['template-agent-summary'] })
      void client.invalidateQueries({ queryKey: ['admin', 'templates'] })
      void client.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}
